import { Typography, Box } from '@mui/material';
import React, { useContext, useState, useEffect } from 'react';
import Content from '../components/Layout/Content';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import {
  handleTimeFilter,
  handleStatusFilter,
  conditionsArrayConvertor,
  extractValueFromCoding,
  getIconFromSubItemsArray,
  calculateDateFilterTimeStamp,
  getStatusFilterParamList,
} from '../shared/helper';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';
import SearchBar from '../components/SearchBar/Search';
import {
  conditionsStatusFilterMenuItems,
  DEFAULT_NULL_REPLACEMENT,
  dateFilterMenuItems,
  NUMBER_OF_ROWS_PER_PAGE,
  CONDITION_CLINICAL_STATUS_LIST,
} from '../shared/constants';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import ApplicationContext from '../context/applicationContext';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { useLazyQuery } from '@apollo/client';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import { useSnackbar } from 'notistack';
import {
  FETCH_ALL_CONDITIONS_QUERY,
  FETCH_CONDITION_DETAILS_BY_ID,
} from '../services/ConditionQueries/ConditionService';
import NotFound from '../components/NotFound/NotFoundPage';
import {
  CONDITIONS,
  CONDITIONS_TABLE_HEADING as tableHeading,
  CONDITIONS_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const dropdownBox = {
  display: 'flex',
  width: '160px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.08)',
  borderRadius: '8px',
  mr: '12px',
  marginTop: '8px',
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};
export const Conditions = () => {
  const [searchValue, setSearchValue] = useState('');
  const [openFlyout, setOpenFlyout] = useState(false);
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [detailsData, setDetailsData] = useState([]);
  const [totalCount, setTotalCount] = useState();
  const [offset, setOffset] = useState(0);
  const { menuItems } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();
  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const [fetchAllConditions, fetchAllConditionsCall] = useLazyQuery(
    FETCH_ALL_CONDITIONS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(CONDITIONS.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        let result = res.fetchAllConditions.conditionList;
        let convetor = JSON.parse(result);
        let data = conditionsArrayConvertor(convetor);
        setRecordedData(data);
        setFilteredData(data);
        setTotalCount(res.fetchAllConditions.count);
      },
    }
  );

  const [fetchConditionById, fetchConditionByIdCall] = useLazyQuery(
    FETCH_CONDITION_DETAILS_BY_ID,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(CONDITIONS.ON_ERROR_TEXT.BY_ID, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchConditionById);
      },
    }
  );

  const handleChangeFilter = () => {
    setOffset(0);
  };

  useEffect(() => {
    fetchAllConditions({
      variables: {
        sourceUrl: '',
        count: NUMBER_OF_ROWS_PER_PAGE.toString(),
        offset: offset,
        dateFilter: calculateDateFilterTimeStamp(timeFilter) || 0,
        statusFilter: getStatusFilterParamList(
          statusFilter,
          conditionsStatusFilterMenuItems,
          CONDITION_CLINICAL_STATUS_LIST
        ),
        searchFilter: searchValue,
      },
    });
  }, [offset, searchValue, timeFilter, statusFilter]);

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
    },
  };
  const statusFilterOptions = {
    options: conditionsStatusFilterMenuItems,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const detailsLoading = fetchConditionByIdCall.loading;

  const showFlyout = result => {
    const parseResult = JSON.parse(result?.condition);
    const tableRowInfo = recordsData.find(obj => obj.id === selectedTableId);
    const modalData = [
      {
        column_name: CONDITIONS.MODAL_DATA.STATUS,
        value: tableRowInfo.status,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.RECORDED_ON,
        value: tableRowInfo.recordedOn,
        width: 6,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.RECORDED_BY,
        value: parseResult?.recorder?.display || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.FACILITY,
        value:
          parseResult?.encounter?.display
            .replaceAll('[', '')
            .replaceAll(']', '') || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.CATEGORY,
        value:
          extractValueFromCoding(parseResult?.category?.[0]) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.CODE,
        value: parseResult?.code?.coding?.[0]?.code || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.SERVERITY,
        value:
          parseResult?.severity?.coding?.[0]?.display ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.SUMMARY,
        value:
          parseResult?.stage?.[0]?.summary?.text ||
          parseResult?.stage?.[0]?.summary?.coding?.[0]?.display ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: CONDITIONS.MODAL_DATA.NOTE,
        value: parseResult?.note?.[0]?.text || DEFAULT_NULL_REPLACEMENT,
        width: 12,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = id => {
    setSelectedTableId(id);
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.condition);
    fetchConditionById({
      variables: {
        conditionId: id,
      },
    });
  };

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{CONDITIONS.TITLE_TEXT}</Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={CONDITIONS.SEARCH_PLACEHOLDER_TEXT}
          setOffset={setOffset}
        />
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...statusFilterOptions} filterType="Status:" />
      </Box>
      <Box sx={table}>
        {!fetchAllConditionsCall.loading && !filteredData.length ? (
          <NotFound title={CONDITIONS.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Medical?.subItems,
                'Conditions'
              )
            : null
        }
        category={CONDITIONS.TITLE_TEXT}
        title={modalTitle}></ServiceFlyout>
      <SimpleBackdrop open={fetchAllConditionsCall.loading || detailsLoading} />
    </Content>
  );
};
